import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash, Storefront, Handshake, Barn, Truck } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/gado";

function CrudList({ resource, idKey, fields, icon: Icon, label }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(fields.reduce((a, f) => ({ ...a, [f.key]: "" }), {}));
  const { data = [], isLoading } = useQuery({
    queryKey: [resource],
    queryFn: async () => (await api.get(`/${resource}`)).data,
  });

  const add = async () => {
    if (!form[fields[0].key]) return toast.error(`Informe ${fields[0].label.toLowerCase()}`);
    try {
      await api.post(`/${resource}`, form);
      qc.invalidateQueries({ queryKey: [resource] });
      setForm(fields.reduce((a, f) => ({ ...a, [f.key]: "" }), {}));
      toast.success(`${label} adicionado`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao adicionar");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/${resource}/${id}`);
      qc.invalidateQueries({ queryKey: [resource] });
      toast.success(`${label} removido`);
    } catch {
      toast.error("Erro ao remover");
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gado-line bg-white p-5">
        <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(160px,1fr))_auto] gap-3 items-end">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="text-xs uppercase tracking-wider text-gado-muted">{f.label}</label>
              <Input
                data-testid={`${resource}-${f.key}-input`}
                value={form[f.key]}
                onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                placeholder={f.label}
                className="mt-1.5"
              />
            </div>
          ))}
          <Button onClick={add} data-testid={`${resource}-add-button`} className="bg-gado-green hover:bg-gado-greenHover text-white h-10">
            <Plus size={18} className="mr-1" /> Adicionar
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-gado-line bg-white divide-y divide-gado-line">
        {isLoading && <p className="p-6 text-gado-muted text-sm">Carregando…</p>}
        {!isLoading && data.length === 0 && (
          <div className="p-10 text-center">
            <Icon size={34} className="mx-auto text-gado-line mb-3" />
            <p className="text-gado-muted">Nenhum {label.toLowerCase()} cadastrado.</p>
          </div>
        )}
        {data.map((item) => (
          <div key={item[idKey]} className="flex items-center justify-between px-5 py-3.5" data-testid={`${resource}-item-${item[idKey]}`}>
            <div>
              <p className="font-semibold text-gado-ink">{item.name}</p>
              <p className="text-sm text-gado-muted">
                {fields.slice(1).map((f) => item[f.key]).filter(Boolean).join(" · ")}
              </p>
            </div>
            <button
              onClick={() => del(item[idKey])}
              data-testid={`${resource}-delete-${item[idKey]}`}
              className="text-gado-muted hover:text-destructive p-2 rounded-md hover:bg-destructive/10 transition-colors"
            >
              <Trash size={18} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const TABS = [
  {
    v: "buyers", label: "Compradores", icon: Storefront, resource: "buyers", idKey: "buyer_id",
    fields: [{ key: "name", label: "Nome" }],
  },
  {
    v: "partners", label: "Parceiros", icon: Handshake, resource: "partners", idKey: "partner_id",
    fields: [{ key: "name", label: "Nome" }],
  },
  {
    v: "farms", label: "Fazendas", icon: Barn, resource: "farms", idKey: "farm_id",
    fields: [{ key: "name", label: "Nome" }, { key: "city", label: "Cidade" }, { key: "district", label: "Povoado" }],
  },
  {
    v: "drivers", label: "Motoristas", icon: Truck, resource: "drivers", idKey: "driver_id",
    fields: [{ key: "name", label: "Nome" }, { key: "phone", label: "Telefone" }, { key: "plate", label: "Placa" }],
  },
];

export default function Cadastros() {
  return (
    <div className="space-y-6" data-testid="cadastros-page">
      <div>
        <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">Cadastros</h1>
        <p className="text-gado-muted">Compradores, parceiros, fazendas e motoristas.</p>
      </div>

      <Tabs defaultValue="buyers">
        <TabsList className="bg-white border border-gado-line p-1 h-auto flex-wrap">
          {TABS.map((t) => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`} className="data-[state=active]:bg-gado-green data-[state=active]:text-white gap-2">
              <t.icon size={16} /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {TABS.map((t) => (
          <TabsContent key={t.v} value={t.v} className="mt-5">
            <CrudList
              resource={t.resource}
              idKey={t.idKey}
              fields={t.fields}
              icon={t.icon}
              label={t.label.replace(/s$/, "")}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
